<template>
  <div :class="b()">
    <!-- Banner Image -->
    <div
      v-if="image"
      :class="b('image')"
    >
      <e-picture
        :sources="image.media"
        :fallback="image.fallback"
        :alt="image.alt"
        :ratio="$viewport.isSm ? 8 / 3 : 3 / 4"
      />
    </div>

    <div :class="b('wrapper', { focus: isOpen })">
      <div
        v-outside-click="close"
        v-focus-item:gradient="isOpen"
        :class="b('content', { open: isOpen })"
      >
        <!-- Title -->
        <div :class="b('title')">
          <span
            v-if="fullUserName"
            :class="b('title-row')"
          >
            {{ $t('c-search-banner.titleRow1', { name: fullUserName }) }}
          </span>
          <!-- eslint-disable vue/no-v-html -->
          <span
            v-html="titleRow2"
            :class="b('title-row')"
          ></span>
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <!-- Search bar -->
        <div :class="b('search-bar')">
          <e-search
            v-model="searchTerm"
            :label="$t('c-search-banner.searchFieldLabel')"
            :icon="searchTerm ? 'i-close' : 'i-search'"
            name="search-field"
            height="400"
            @focus="isOpen = true"
            @reset="close"
            @enter="submit"
          >
            <template #activeAction>
              <e-button
                v-if="$viewport.isSm"
                width="auto"
                negative
                @click="submit"
              >
                {{ $t('c-search-banner.submitSearchButtonText') }}
              </e-button>
            </template>
          </e-search>
        </div>

        <!-- Focus content block -->
        <transition name="fade">
          <div
            v-if="isOpen"
            :class="b('focus-block')"
          >
            <!-- Search results -->
            <div
              v-if="showSearchResults"
              :class="b('result-block', { left: true })"
            >
              <div :class="b('result-head')">
                <c-tabs
                  v-if="sessionStore.isCartEnabled"
                  :show-as-collapse-on-mobile="false"
                >
                  <c-tab-panel
                    :name="$t('c-search-banner.tabShop')"
                    :selected="activeTab === 'shop'"
                    :is-subtle="
                      !searchStore.runningRequests.apiFetchSearchSuggestions &&
                      !searchStore.searchSuggestionsCommerce.length
                    "
                    @change="activeTab = 'shop'"
                  />
                  <c-tab-panel
                    :name="$t('c-search-banner.tabWebsite')"
                    :selected="activeTab === 'website'"
                    :is-subtle="
                      !searchStore.runningRequests.apiFetchSearchSuggestions &&
                      !searchStore.searchSuggestionsPimcore.length
                    "
                    @change="activeTab = 'website'"
                  />
                </c-tabs>
                <div
                  v-else
                  :class="b('result-title')"
                >
                  {{ $t('c-search-banner.searchResultsTitle') }}
                </div>
              </div>
              <div :class="b('result-block-inner')">
                <e-scrollable
                  v-if="searchStore.searchSuggestionsCommerce.length || searchStore.searchSuggestionsPimcore.length"
                >
                  <div v-show="activeTab === 'shop'">
                    <ul
                      v-if="searchStore.searchSuggestionsCommerce.length"
                      :class="b('results-list')"
                    >
                      <li
                        v-for="item in searchStore.searchSuggestionsCommerce"
                        :key="item.id"
                        :class="b('results-item')"
                      >
                        <c-search-suggestion :item="item" />
                      </li>
                    </ul>
                    <i18n-t
                      v-else
                      :class="b('no-results-text')"
                      keypath="c-search-banner.noResultsTextShopTab"
                      scope="global"
                    >
                      <template #link>
                        <a :href="sessionStore.urls.noSearchResultContactPage">
                          {{ $t('c-search-banner.noResultsLinkTextShopTab') }}
                        </a>
                      </template>
                    </i18n-t>
                  </div>
                  <div v-show="activeTab === 'website'">
                    <ul
                      v-if="searchStore.searchSuggestionsPimcore.length"
                      :class="b('results-list')"
                    >
                      <li
                        v-for="item in searchStore.searchSuggestionsPimcore"
                        :key="item.id"
                        :class="b('results-item')"
                      >
                        <c-search-suggestion :item="item" />
                      </li>
                    </ul>
                    <i18n-t
                      v-else
                      :class="b('no-results-text')"
                      keypath="c-search-banner.noResultsTextWebsiteTab"
                      scope="global"
                    >
                      <template #link>
                        <a :href="sessionStore.urls.noSearchResultContactPage">
                          {{ $t('c-search-banner.noResultsLinkTextWebsiteTab') }}
                        </a>
                      </template>
                    </i18n-t>
                  </div>
                </e-scrollable>
                <div
                  v-if="searchStore.runningRequests.apiFetchSearchSuggestions"
                  :class="b('loader')"
                >
                  <e-progress />
                </div>
              </div>
            </div>

            <!-- Related results -->
            <div :class="b('result-block', { right: true })">
              <div :class="b('result-head')">
                <div :class="b('result-title')">
                  {{ $t('c-search-banner.relatedResultsTitle') }}
                </div>
              </div>
              <div :class="b('result-block-inner')">
                <e-scrollable>
                  <ul :class="b('results-list')">
                    <li
                      v-for="link in relatedResults"
                      :key="link.url"
                      :class="b('results-item')"
                    >
                      <a
                        :href="link.url"
                        :target="link.target"
                        :class="b('link')"
                      >
                        {{ link.title }}
                      </a>
                    </li>
                  </ul>
                </e-scrollable>
              </div>
            </div>
          </div>
        </transition>

        <!-- Close Icon -->
        <button
          v-if="isOpen"
          :class="b('close-button')"
          :title="$t('c-search-banner.closeButtonTitle')"
          type="button"
          @click="close"
        >
          <span class="invisible">{{ $t('c-search-banner.closeButtonTitle') }}</span>
          <e-icon-sprite icon="i-close" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import useSearchStore from '@/stores/search';
  import useSessionStore from '@/stores/session';
  import cSearchSuggestion from '@/components/c-search-suggestion.vue';
  import cTabPanel from '@/components/c-tab-panel.vue';
  import cTabs from '@/components/c-tabs.vue';
  import eButton from '@/elements/e-button.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import ePicture from '@/elements/e-picture.vue';
  import eProgress from '@/elements/e-progress.vue';
  import eScrollable from '@/elements/e-scrollable.vue';
  import eSearch from '@/elements/e-search.vue';

  /**
   * This area brick component renders a banner image with a search bar.
   */
  export default {
    name: 'c-search-banner',

    components: {
      eIconSprite,
      eProgress,
      cTabPanel,
      cTabs,
      eButton,
      ePicture,
      cSearchSuggestion,
      eScrollable,
      eSearch,
    },
    // mixins: [],

    props: {
      /**
       * Holds the image of the component.
       */
      image: {
        type: Object,
        default: null,
      },

      /**
       * Gets the list of links for the related results section.
       */
      relatedResults: {
        type: Array,
        default: () => [],
      },

      /**
       * Defines the second row of the title.
       */
      titleRow2: {
        type: String,
        default: '',
      },
    },
    setup() {
      return {
        searchStore: useSearchStore(),
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} Defines the open state of the focus content.
         */
        isOpen: false,

        /**
         * @type {String} Holds the current value of the search input field.
         */
        searchTerm: '',

        /**
         * @type {Number|null} A Number, representing the ID value of the timer that is set.
         */
        debouncedTimeout: null,

        /**
         * @type {Number} The debounce time in milliseconds.
         */
        debounceValue: 300,

        /**
         * @type {String|null} Holds the currently active tab.
         */
        activeTabInternal: null,

        /**
         * @type {Boolean} Defines if search results have been changed.
         */
        isDirty: false,
      };
    },

    computed: {
      /**
       * Defines if the search result block should be visible.
       *
       * @returns {Boolean}
       */
      showSearchResults() {
        return this.searchTerm.length > 2 && this.isDirty;
      },

      /**
       * Gets the full name (first- and lastName) if there is a logged in user.
       *
       * @returns {String}
       */
      fullUserName() {
        const { firstName, lastName } = this.sessionStore.user || {};

        return firstName && lastName ? `${firstName} ${lastName}` : '';
      },

      /**
       * Gets/sets the active search tab.
       */
      activeTab: {
        get() {
          if (!this.sessionStore.isCartEnabled) {
            return 'website';
          }

          if (this.activeTabInternal) {
            return this.activeTabInternal;
          }

          return this.searchStore.searchSuggestionsPimcore.length && !this.searchStore.searchSuggestionsCommerce.length
            ? 'website'
            : 'shop';
        },
        set(activeTab) {
          this.activeTabInternal = activeTab;
        },
      },
    },
    watch: {
      /**
       * Observes the search term and fetches the suggestions with the defined debounce.
       *
       * @param {String} term - The search term.
       */
      searchTerm(term) {
        if (term && term.length >= 3) {
          const amount = this.$viewport.isMd ? 10 : 5;

          if (this.debounceValue > 0) {
            clearTimeout(this.debouncedTimeout);

            this.debouncedTimeout = setTimeout(
              () =>
                this.searchStore.apiFetchSearchSuggestions({ term, amount }).finally(() => {
                  this.debouncedTimeout = null;
                  this.isDirty = true;
                }),
              this.debounceValue,
            );
          } else {
            this.searchStore.apiFetchSearchSuggestions({ term, amount }).finally(() => {
              this.isDirty = true;
            });
          }
        } else {
          this.isDirty = false;
          this.searchStore.searchSuggestionsPimcore = [];
          this.searchStore.searchSuggestionsCommerce = [];
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Close event handler.
       */
      close() {
        if (this.isOpen) {
          this.isOpen = false;
          this.searchTerm = '';
          this.isDirty = false;
          this.searchStore.searchSuggestionsPimcore = [];
          this.searchStore.searchSuggestionsCommerce = [];
        }
      },

      /**
       * Submits the search.
       */
      submit() {
        if (this.searchTerm) {
          if (this.sessionStore.isCartEnabled && this.activeTab === 'shop') {
            this.searchStore.submitSearchShop({ query: this.searchTerm });

            return;
          }

          this.searchStore.submitSearchWebsite({ query: this.searchTerm });
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-search-banner {
    $this: &;
    $ie-scrollbar-width: 17px;

    position: relative;
    padding: functions.rem(variables.$spacing--40 0);

    @include mixins.media(sm) {
      padding: functions.rem(variables.$spacing--100 0);
    }

    &__image {
      @extend %fullwidth--content;

      position: absolute;
      top: 0;
      height: 100%;

      .e-picture {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      img {
        @supports (object-fit: cover) {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        @supports not (object-fit: cover) {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }

    &__wrapper {
      @extend %border-gradient;

      position: relative;
      width: 100%;
      min-height: functions.rem(350px);
      padding: functions.rem(variables.$spacing--40);

      @include mixins.media(sm) {
        min-height: functions.rem(500px);
      }

      @include mixins.media(md) {
        min-height: functions.rem(450px);
      }
    }

    &__wrapper--focus {
      border-color: transparent;
      border-image: none;
    }

    &__title {
      @extend %heading-h1;

      margin-bottom: 0.3em;
      color: variables.$color-grayscale--1000;

      @include mixins.media(sm) {
        color: variables.$color-primary--1;
      }
    }

    &__title-row {
      display: block;
    }

    &__search-bar {
      max-width: functions.rem(600px);
      margin-top: functions.rem(variables.$spacing--30);

      @include mixins.media(sm) {
        margin-top: functions.rem(variables.$spacing--50);
      }

      .e-search__field {
        &::-webkit-input-placeholder {
          // WebKit, Blink, Edge
          color: variables.$color-primary--1;
        }

        &:-moz-placeholder {
          // Mozilla Firefox 4 to 18
          color: variables.$color-primary--1;
        }

        &::placeholder {
          // Most modern browsers support this now
          color: variables.$color-primary--1;
        }
      }

      .e-button {
        position: absolute;
        top: 0;
        right: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        max-width: functions.rem(150px);
        height: 100%;
      }

      .e-button:active:not([disabled]) {
        position: absolute;
      }

      .e-search__label {
        @include mixins.media(sm) {
          padding-right: functions.rem(150px);
        }
      }
    }

    &__content {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      min-height: 100%;
      padding: functions.rem(variables.$spacing--20);
      transition: background variables.$transition-duration--200 ease-in-out;

      @include mixins.media(sm) {
        justify-content: flex-start;
        padding: functions.rem(variables.$spacing--40);
      }
    }

    &__content--open {
      justify-content: flex-start;
      background: variables.$color-grayscale--1000;

      #{$this}__title,
      #{$this}__close-button {
        @include mixins.media($down: xs) {
          display: none;
        }
      }

      #{$this}__search-bar {
        @include mixins.media($down: xs) {
          margin-top: 0;
        }
      }
    }

    &__focus-block {
      max-height: functions.rem(500px);
      margin-top: functions.rem(variables.$spacing--40);
      overflow: auto;

      @include mixins.media(sm) {
        display: flex;
        margin-top: functions.rem(variables.$spacing--60);
      }
    }

    &__close-button {
      @extend %button-reset;

      position: absolute;
      top: functions.rem(variables.$spacing--40);
      right: functions.rem(variables.$spacing--40);
    }

    &__result-block {
      @include mixins.media(sm) {
        flex: 1 0 50%;
        max-width: 50%;
      }
    }

    &__result-block--left {
      margin-bottom: functions.rem(variables.$spacing--40);

      @include mixins.media(sm) {
        margin-bottom: 0;
      }

      #{$this}__result-block-inner {
        @include mixins.media(sm) {
          padding-right: functions.rem(variables.$spacing--50);
        }
      }
    }

    &__result-block-inner {
      position: relative;
      min-height: functions.rem(200px);

      @include mixins.media(sm) {
        height: functions.rem(250px);
      }
    }

    &__result-head {
      display: flex;
      align-items: flex-end;
      height: functions.rem(60px);
    }

    &__result-title {
      @extend %heading-h3;
      @include mixins.font(variables.$font-size--12, 18px, variables.$font-weight--regular);

      display: block;
      width: 100%;
      margin-bottom: 0;
      padding: functions.rem(variables.$spacing--15 0);
      border-bottom: 1px solid variables.$color-grayscale--900;
      color: variables.$color-grayscale--100;

      @include mixins.media(sm) {
        @include mixins.font(variables.$font-size--16, 24px, variables.$font-weight--semi-bold);
      }
    }

    &__results-list {
      @extend %list-reset;

      display: flex;
      flex-wrap: wrap;
      margin-top: functions.rem(variables.$spacing--20);

      @include mixins.media(sm) {
        margin-top: functions.rem(variables.$spacing--40);
        padding-right: functions.rem(variables.$spacing--20);
      }
    }

    &__results-item {
      flex: 1 0 100%;
      max-width: 100%;
      margin-bottom: functions.rem(variables.$spacing--15);

      @include mixins.media(md) {
        flex: 1 0 50%;
        max-width: 50%;
        margin-bottom: functions.rem(variables.$spacing--25);
        padding-right: functions.rem(variables.$spacing--25);
      }
    }

    &__link {
      @include mixins.font(variables.$font-size--16, 24px, variables.$font-weight--regular);

      display: block;
      color: variables.$color-primary--1;

      @include mixins.media(sm) {
        @include mixins.font(variables.$font-size--18, 24px, variables.$font-weight--semi-bold);
      }

      &:hover,
      &:focus {
        color: variables.$color-primary--2;
      }
    }

    &__submit-search-button {
      @extend %button-reset;

      border-radius: 50%;
      background-color: variables.$color-primary--1;
    }

    &__loader {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      width: 100%;
      height: 100%;
      padding-top: functions.rem(variables.$spacing--20);
      background: rgba(variables.$color-grayscale--1000, 0.8);

      @include mixins.media(sm) {
        align-items: center;
        padding-top: functions.rem(variables.$spacing--40);
        padding-right: functions.rem(variables.$spacing--40);
      }
    }

    &__no-results-text {
      @include mixins.font(variables.$font-size--18, variables.$line-height--32);

      display: block;
      padding-top: functions.rem(variables.$spacing--60);
      color: variables.$color-primary--1;
    }

    .c-tabs {
      width: 100%;
      padding: 0;
    }

    .c-tab-panel {
      padding: 0;
    }

    .c-search-suggestion__price {
      @include mixins.media($down: xs) {
        display: none; // Don't show price on mobile because of the limited amount of space.
      }
    }

    // Vue transitions
    .fade-enter-active,
    .fade-leave-active {
      transition: all variables.$transition-duration--200 ease-in-out;
    }

    .fade-enter-from,
    .fade-leave-to {
      opacity: 0;
    }
  }
</style>
